import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../components/AppDataGrid';
import { AppToolbar, type ExportData } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import { TopbarStepper } from '../../components/StepIsland/TopbarStepper';
import { type IslandStep } from '../../components/StepIsland/StepIsland';
import { useConfirm, showDialog } from '../../components/confirm';
import { useNotify } from '../../components/notifications';
import { StepHint } from '../../components/StepHint/StepHint';
import { fmtLocalDateTime } from '../../utils/datetime';
import { useNavActions } from '../../hooks/useNavActions';
import '../backlog/backlog.css';

/** The Result Tracking list row that "Load" opens.  In QCBatchID mode `id` is the
 *  analytical batch Oid (SpreadSheetEntry_AnalyticalBatch.Oid); in JobID mode (`isJob`)
 *  `id` is the job's Samplecheckin.Oid and the detail loads ALL the job's results. */
export interface ResultTrackingBatch {
  id:        string;
  qcBatchID: string | null;
  client:    string | null;
  projectID: string | null;
  jobID:     string | null;
  /** JobID-mode Load — `id` is a Samplecheckin.Oid; fetch the whole-job results. */
  isJob?:    boolean;
}

// Per-sample result row — the full ALPACA ResultView column set returned by
// AI_Analysis_ResultTracking_Samples_SP.  `isQc` drives the Sample/QC "Selection
// Mode" filter.
interface TrackSampleRow {
  id:                string;
  qcBatchID:         string | null;
  sys:               string | null;
  siteName:          string | null;
  samplingAddress:   string | null;
  test:              string | null;
  method:            string | null;
  parameter:         string | null;
  numericResult:     string | null;
  result:            string | null;
  rawData:           string | null;
  units:             string | null;
  df:                string | null;
  finalVolume:       string | null;
  rec:               string | null;
  rpd:               string | null;
  spikeAmount:       string | null;
  spikeAmountUnit:   string | null;
  recHCLimit:        string | null;
  recLCLimit:        string | null;
  rpdHCLimit:        string | null;
  rpdLCLimit:        string | null;
  rl:                string | null;
  loq:               string | null;
  uql:               string | null;
  analyzedDate:      string | null;
  analyzedBy:        string | null;
  sdmsValidatedDate: string | null;
  sdmsValidatedBy:   string | null;
  statusText:        string | null;
  statusId:          string | null;
  validatedDate:     string | null;
  validatedBy:       string | null;
  approvedDate:      string | null;
  approvedBy:        string | null;
  matrix:            string | null;
  methodNumber:      string | null;
  component:         string | null;
  client:            string | null;
  qcType:            string | null;
  isQc:              boolean;
}

// ALPACA parity: empty cells render BLANK (no '—' placeholder).
function dt(v?: string | null): string {
  return v ? (fmtLocalDateTime(v) || '') : '';
}
function txt(field: keyof TrackSampleRow, header: string, width?: number): AppColumn<TrackSampleRow> {
  return { field: field as string, header, ...(width ? { width } : {}),
    text: r => (r[field] as string | null) ?? '', render: r => (r[field] as string | null) ?? '' };
}
function dcol(field: keyof TrackSampleRow, header: string): AppColumn<TrackSampleRow> {
  return { field: field as string, header,
    text: r => dt(r[field] as string | null), render: r => dt(r[field] as string | null) };
}

// ONE column set for both Sample and QC modes, in ALPACA's ResultView order
// (SampleParameter_ListView_Copy_ResultView_SingleChoice, verified against the
// legacy Model.xafml ColumnInfo indexes).  First two columns carry widths for the
// frozen-column offsets (stickyColumns={2}).
const COLS: AppColumn<TrackSampleRow>[] = [
  txt('qcBatchID',       'QcBatchID', 140),
  txt('sys',             'SysSampleCode', 150),
  txt('siteName',        'SiteName'),
  txt('samplingAddress', 'SamplingAddress'),
  txt('test',            'Test'),
  txt('method',          'Method'),
  txt('parameter',       'Parameter'),
  txt('numericResult',   'NumericResult'),
  txt('result',          'Result'),
  txt('rawData',         'RawData'),
  txt('units',           'Units'),
  txt('df',              'D.F'),
  txt('finalVolume',     'Volume'),
  txt('rec',             '%Rec'),
  txt('rl',              'RL'),
  txt('loq',             'LowSTD'),
  txt('uql',             'HighSTD'),
  dcol('analyzedDate',   'AnalyzedDate'),
  txt('analyzedBy',      'AnalyzedBy'),
  dcol('sdmsValidatedDate', 'SDMSValidatedDate'),
  txt('sdmsValidatedBy', 'SDMSValidatedBy'),
  { field: 'statusText', header: 'Status', type: 'status', text: r => r.statusText ?? '' },
  txt('statusId',        'StatusID'),
  dcol('validatedDate',  'ResultValidatedDate'),
  txt('validatedBy',     'ResultValidatedBy'),
  dcol('approvedDate',   'ResultApprovedDate'),
  txt('approvedBy',      'ResultApprovedBy'),
  txt('matrix',          'Matrix'),
  txt('methodNumber',    'MethodNumber'),
  txt('component',       'Component'),
  txt('client',          'Client'),
];

// QCType column — ALPACA shows it ONLY in the "QC Sample" selection mode
// (SampleParameter_ListView_Copy_ResultView_SingleChoice QCType, PropertyName
// QCBatchID.QCType.QCTypeName), positioned right after QcBatchID and BEFORE
// SysSampleCode (ALPACA column order, verified 2026-07-25).  It lists the
// control type (CCV High/Low · Dup · LCS · Method Blank · MS).  Hidden in Sample mode.
const QCTYPE_COL: AppColumn<TrackSampleRow> = txt('qcType', 'QCType', 130);

// QC-mode-only recovery / RPD / spike columns — ALPACA shows these ONLY in the
// "QC Sample" selection mode (ResultView indexes 18-21 + SpikeAmount/Unit),
// appended after the shared columns.  Hidden in Sample mode.
const QC_STAT_COLS: AppColumn<TrackSampleRow>[] = [
  txt('recHCLimit',      '%HighRec'),
  txt('recLCLimit',      '%LowRec'),
  txt('rpd',             '%RPD'),
  txt('rpdHCLimit',      'HighRPD'),
  txt('rpdLCLimit',      'LowRPD'),
  // SpikeAmount is a numeric value → right-aligned (ALPACA parity).
  { field: 'spikeAmount', header: 'SpikeAmount', align: 'right',
    text: r => r.spikeAmount ?? '', render: r => r.spikeAmount ?? '' },
  txt('spikeAmountUnit', 'SpikeAmountUnit'),
];

interface Props {
  batch: ResultTrackingBatch;
  /** Return to the Result Tracking list. */
  onClose: () => void;
}

/**
 * Result Tracking → "Load" drill-in (READ-ONLY).  Ports ALPACA's Result Tracking
 * Retrieve: the selected QC batch's individual result rows in ONE grid, the full
 * ALPACA ResultView column set, with a "Selection Mode" dropdown (Sample / QC) and
 * a leading row-selection checkbox column — matching ALPACA's
 * SampleParameter_ListView_Copy_ResultView_SingleChoice.
 */
export default function ResultTrackingDetail({ batch, onClose }: Props) {
  const [rows, setRows]       = useState<TrackSampleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs]   = useState<number | null>(null);
  const [error, setError]     = useState<string | null>(null);
  // ALPACA "Selection Mode" — Sample (default) or QC.  Filters the single grid's
  // rows (isQc); the column set is the SAME for both modes.
  const [mode, setMode]       = useState<'Sample' | 'QC'>('Sample');
  // Leading checkbox column (ALPACA parity) — also drives the Rollback action.
  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const notify = useNotify();
  const { prompt } = useConfirm();

  // Rollback is a WRITE action — show it only when the user's Module Access
  // Control grants Write on Result Tracking (or they're an admin).  Uses the
  // shared per-form action gate (navActionsForForm('restrack') → Write); the
  // server enforces the same rule with a 403, so this is UX only.
  const canRollback = useNavActions('restrack').write;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const t0 = performance.now();
    try {
      // JobID mode loads the whole job's rows; QCBatchID mode loads one batch.
      const url = batch.isJob
        ? `/api/v1/analysis/result-tracking/job/${batch.id}/results`
        : `/api/v1/analysis/result-tracking/${batch.id}/results`;
      const r = await api.get(url);
      setRows((r.data as { data: TrackSampleRow[] }).data ?? []);
      setSelectedIds(new Set());
    } catch {
      setRows([]);
      setError('Failed to load results — check the API logs.');
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [batch.id, batch.isJob]);

  useEffect(() => { void load(); }, [load]);

  // Clear the row selection when the Selection Mode switches (Sample ↔ QC) — otherwise a
  // tick made in one mode stays selected but INVISIBLE in the other, so Rollback would act
  // on rows the user can't see.  (Bug fix 2026-07-27.)
  useEffect(() => { setSelectedIds(new Set()); }, [mode]);

  // Browser Back closes the detail (parity with the Result Validation detail).
  useEffect(() => {
    window.history.pushState({ rtDetail: true }, '');
    const onPop = () => onClose();
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [onClose]);

  // Rollback the ticked result rows (safe subset) — ALPACA ResultEntryViewController
  // Rollback_Execute: guard pre-check (fires BEFORE the reason popup) → mandatory
  // reason → revert to Pending Entry.  Reported rows are skipped server-side.
  const doRollback = useCallback(async () => {
    const ids = Array.from(selectedIds).map(String);
    if (ids.length === 0) { notify.info('Please select one or more result rows to roll back.'); return; }
    try {
      const pre = await api.post('/api/v1/analysis/result-tracking/rollback/precheck', { sampleParameterIds: ids });
      const d = pre.data as { blocked?: boolean; message?: string; eligible?: number; skippedReported?: number };
      if (d.blocked) { notify.warning(d.message ?? 'Rollback is blocked.'); return; }
      if ((d.eligible ?? 0) === 0) {
        notify.warning(d.skippedReported
          ? 'The selected rows are already Reported and cannot be rolled back.'
          : 'Nothing to roll back in the selected rows.');
        return;
      }
    } catch { /* precheck hiccup — the write path re-checks the guards */ }
    const reason = await prompt({
      title: 'Result Rollback',
      message: 'Enter a reason for the rollback. The selected results return to Pending Entry.',
      placeholder: 'Rollback reason…',
      required: true,
      confirmLabel: 'Rollback',
    });
    if (reason === null) return;
    try {
      const res = await api.post('/api/v1/analysis/result-tracking/rollback', { sampleParameterIds: ids, reason });
      const d = res.data as { success: boolean; error?: string; rolledBack?: number; skippedReported?: number };
      if (!d.success) { notify.error(d.error ?? 'Rollback failed.'); return; }
      if ((d.rolledBack ?? 0) > 0) {
        // Milestone (§18.6) → centered modal, not the Tier-2 top banner.
        void showDialog({
          type: 'success',
          title: 'Rollback',
          message: `Rolled back successfully.${d.skippedReported ? ` (${d.skippedReported} Reported skipped.)` : ''}`,
        });
        setSelectedIds(new Set());
        void load();
      } else {
        notify.warning('Nothing was rolled back (Reported rows are skipped).');
      }
    } catch {
      notify.error('Rollback failed — check the API logs.');
    }
  }, [selectedIds, notify, prompt, load]);

  const sampleRows = useMemo(() => rows.filter(r => !r.isQc), [rows]);
  const qcRows     = useMemo(() => rows.filter(r =>  r.isQc), [rows]);
  const activeRows = mode === 'Sample' ? sampleRows : qcRows;
  // QC mode inserts the QCType column right after QcBatchID and BEFORE SysSampleCode
  // (ALPACA column order: QcBatchID · QCType · SysSampleCode · …); Sample mode uses
  // the base set.
  const cols = useMemo(
    () => (mode === 'QC'
      ? [...COLS.slice(0, 1), QCTYPE_COL, ...COLS.slice(1), ...QC_STAT_COLS]
      : COLS),
    [mode]);
  // Header id — JobID in job mode, QCBatchID in batch mode (ALPACA {BO} – {record}).
  const headId = (batch.isJob ? batch.jobID : batch.qcBatchID) ?? '';
  const exportData: ExportData = {
    filename: `result-tracking-${headId || batch.id}-${mode.toLowerCase()}`,
    ...gridExportMatrix(activeRows, cols),
  };
  // Identity line — Client · ProjectID · Job (blank parts omitted).
  const identity = [batch.client, batch.projectID, batch.jobID ? `Job ${batch.jobID}` : null]
    .filter(Boolean).join('  ·  ');

  return (
    <div className="bl-page">
      {/* Dynamic-Island stepper (BO UI Standard §5) — on the detail both steps are
          done: Select Batch ✓ → Load Samples ✓ (prototype Result-Entry). */}
      <TopbarStepper steps={[
        { id: 'select', label: 'Select Batch', state: 'done' },
        { id: 'load',   label: 'Load Samples', state: 'done' },
      ] as IslandStep[]} />
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Analysis</span><i className="ti ti-chevron-right" />
            <span onClick={onClose} style={{ cursor: 'pointer' }}>Result Tracking</span>
            <i className="ti ti-chevron-right" />
            <span className="dp-bc-active">{headId || '—'}</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="restrack" title={`Result Tracking – ${headId}`}
              loadMs={loadMs} loading={loading} auditFormName="Result Tracking" />
            {/* TOP-RIGHT actions (Result Approval parity): Back · Rollback. */}
            <div className="dp-title-actions">
              <button className="ca-btn" onClick={onClose} title="Back to the Result Tracking list">
                <i className="ti ti-arrow-left" /> Back
              </button>
              {/* Rollback is Write-gated: hidden for read-only users (their MAC
                  grants no Write on Result Tracking).  The endpoint enforces the
                  same rule, so this is UX only. */}
              {canRollback && (
                <button className="ca-btn" onClick={() => void doRollback()} disabled={selectedIds.size === 0}
                  title={selectedIds.size === 0
                    ? 'Tick one or more result rows to roll back.'
                    : 'Roll the ticked results back to Pending Entry (reason required).'}>
                  <i className="ti ti-arrow-back-up" style={{ color: 'var(--ai-red)' }} /> Rollback
                </button>
              )}
            </div>
          </div>
          <div className="dp-subtitle">
            {'Read-only'}{identity ? '  ·  ' : ''}{identity}
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        {/* Guidance strip — prototype (Result-Entry) detail recHint content, AI-accurate
            (Pending Entry).  Shared <StepHint> style, matching the list. */}
        <StepHint tone="guidance">
          <i className="ti ti-info-circle" /> <b>Tracking · read-only.</b> The full result record for
          this batch. Use <b>Rollback</b> (mandatory reason, audited) to return the results to
          <b> Pending Entry</b> for re-entry.
        </StepHint>
        {/* Toolbar — Selection Mode (Sample/QC) · Refresh · Export to.  Read-only:
            no New/Edit/Delete (ALPACA's Rollback/Comment are write actions, omitted). */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <div className="sa-date-wrap" style={{ marginRight: 4 }}>
                <i className="ti ti-filter sa-date-prefix" />
                <span className="sa-date-label">Selection Mode</span>
                <select className="sa-date-sel" value={mode}
                  onChange={e => setMode(e.target.value as 'Sample' | 'QC')}>
                  <option value="Sample">Sample ({sampleRows.length})</option>
                  <option value="QC">QC Sample ({qcRows.length})</option>
                </select>
              </div>
            }
            onRefresh={load} refreshing={loading} exportData={exportData} />
        </div>

        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-empty" style={{ color: 'var(--ai-red)', flexDirection: 'column', gap: 6 }}>
              <i className="ti ti-alert-triangle" style={{ fontSize: 24, opacity: .35 }} />
              <div>{error}</div>
            </div>
          ) : (
            <AppDataGrid<TrackSampleRow>
              rows={activeRows}
              columns={cols}
              getRowId={(r) => r.id}
              loading={loading}
              loadMs={loadMs}
              selectable
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              stickyColumns={2}
              emptyText={mode === 'Sample' ? 'No sample results in this batch.' : 'No QC results in this batch.'}
            />
          )}
        </div>
      </div>
    </div>
  );
}
